import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useLang, waLink } from "@/i18n";
import { TOURS } from "@/data";
import { Reveal, Kicker } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl bg-[#0B0C10]/70 border border-gold/20 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/40 transition-colors";

const Enquiry = ({ selectedTour }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", tour: "", travel_date: "", guests: "2", hotel_tier: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (selectedTour) setForm((f) => ({ ...f, tour: selectedTour }));
  }, [selectedTour]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
      toast.success(t.enquiry.success);
    } catch {
      toast.error(t.chat.error);
    } finally {
      setSending(false);
    }
  };

  const waSummary = `Namaste Explore India! Enquiry from ${form.name || "a guest"}. Tour: ${form.tour || "Custom itinerary"}. Date: ${form.travel_date || "Flexible"}. Guests: ${form.guests}. ${form.message || ""}`;

  return (
    <section id="enquiry" className="relative py-24 sm:py-32 bg-ink/40 scroll-mt-20" data-testid="enquiry-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-5 gap-14">
        <Reveal className="lg:col-span-2">
          <Kicker>{t.enquiry.kicker}</Kicker>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-50 leading-tight">
            {t.enquiry.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">{t.enquiry.sub}</p>
          <div className="mt-10 relative hidden lg:block">
            <div className="overflow-hidden rounded-2xl ring-1 ring-gold/25">
              <img src="/assets/udaipur-lake.jpg" alt="Lakes of Udaipur" className="h-72 w-full object-cover" />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form onSubmit={submit} className="glass-card rounded-2xl border border-gold/15 p-6 sm:p-10" data-testid="enquiry-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <input required data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder={t.enquiry.fields.name} className={inputCls} />
              <input type="email" data-testid="enquiry-email-input" value={form.email} onChange={set("email")} placeholder={t.enquiry.fields.email} className={inputCls} />
              <input data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder={t.enquiry.fields.phone} className={inputCls} />
              <select data-testid="enquiry-tour-select" value={form.tour} onChange={set("tour")} className={inputCls}>
                <option value="">{t.enquiry.fields.tour}</option>
                {TOURS.map((tour) => (
                  <option key={tour.id} value={tour.title.en}>{tour.title.en}</option>
                ))}
                <option value="Custom">Custom / Tailor-made</option>
              </select>
              <input type="date" data-testid="enquiry-date-input" value={form.travel_date} onChange={set("travel_date")} className={`${inputCls} [color-scheme:dark]`} />
              <select data-testid="enquiry-guests-select" value={form.guests} onChange={set("guests")} className={inputCls}>
                {[1, 2, 3, 4, 5, 6, "7+"].map((g) => (
                  <option key={g} value={g}>{g} {t.enquiry.fields.guests}</option>
                ))}
              </select>
              <select data-testid="enquiry-tier-select" value={form.hotel_tier} onChange={set("hotel_tier")} className={`${inputCls} sm:col-span-2`}>
                <option value="">{t.enquiry.fields.tier}</option>
                {t.enquiry.tiers.map((tier) => (
                  <option key={tier} value={tier}>{tier}</option>
                ))}
              </select>
            </div>
            <textarea
              data-testid="enquiry-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder={t.enquiry.fields.message}
              rows={4}
              className={`${inputCls} mt-5 resize-none`}
            />
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-form-submit-btn"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-amber px-8 py-3.5 text-sm font-bold text-obsidian hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-shadow disabled:opacity-60"
              >
                <Send size={15} />
                {sending ? t.enquiry.sending : t.enquiry.submit}
              </button>
              {done && (
                <a
                  data-testid="enquiry-whatsapp-continue"
                  href={waLink(1, waSummary)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3.5 text-sm font-semibold text-gold-light hover:bg-gold/10 transition-colors"
                >
                  {t.enquiry.whatsappCta}
                </a>
              )}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Enquiry;
